import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { toast } from "@shared/ui/use-toast";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import {
  AssetDimensionPayload,
  AssetFailurePayload,
  AssetTypePayload,
  createAssetClass,
  createAssetDimension,
  createAssetFailure,
  createAssetType,
  deleteAssetClass,
  deleteDimension,
  getAllAssetTypes,
  getAssetClassList,
  getAssetFailureList,
  getAssetTypeList,
  getDimensionList,
  getTypeDimensionList,
  searchAssetClassList,
  toggleConditionParameter,
  updateAssetClass,
  updateAssetDimension,
  updateAssetFailure,
  updateAssetType,
} from "./api";

// Asset Class Hooks
export const useAssetClassList = (showInactive: boolean = true) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.settings.assetClassSettingsList(showInactive),
    () => getAssetClassList(showInactive)
  );
};

export const useCreateAssetClass = () => {
  return useSmartMutation((payload: any) => createAssetClass(payload), {
    onSuccess: () => {
      toast({
        title: "Asset class created successfully!",
        variant: "default",
      });

      const key = QueryKeyFactory.module.ax.settings
        .assetClassList()
        .slice(0, 4);
      const searchKey = QueryKeyFactory.module.ax.settings
        .assetSearchClassList("")
        .slice(0, 4);
      queryClient.invalidateQueries({
        queryKey: key,
      });

      queryClient.invalidateQueries({
        queryKey: searchKey,
      });
    },
    onError: (error: Error) => {
      toast({
        title: `Failed to create asset class: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};

export const useUpdateAssetClass = () => {
  return useSmartMutation((payload: any) => updateAssetClass(payload), {
    onSuccess: () => {
      toast({
        title: "Asset class updated successfully!",
        variant: "default",
      });

      const key = QueryKeyFactory.module.ax.settings.assetClassList();
      queryClient.invalidateQueries({
        queryKey: key,
      });

      queryClient.refetchQueries({
        queryKey: key,
      });
    },
    onError: (error: Error) => {
      toast({
        title: `Failed to update asset class: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};

// Asset Type Hooks
export const useAssetTypeList = (code: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.settings.assetTypeList(code),
    () => getAssetTypeList(code),
    {
      enabled: Boolean(code),
    }
  );
};

export const useAllAssetTypes = () => {
  return useSmartQuery(
    [QueryKeyFactory.module.ax.settings.allAssetTypes()],
    () => getAllAssetTypes()
  );
};

export const useCreateAssetType = () => {
  return useSmartMutation<any, Error, AssetTypePayload>(
    (payload: AssetTypePayload) => createAssetType(payload),
    {
      onSuccess: () => {
        toast({
          title: "Asset type created successfully!",
          variant: "default",
        });

        const assetTypeKey = QueryKeyFactory.module.ax.settings
          .assetTypeList()
          .slice(0, 4);
        const typeDimension = QueryKeyFactory.module.ax.settings
          .assetTypeDimensionList()
          .slice(0, 4);
        const assetClassKey = QueryKeyFactory.module.ax.settings
          .assetClassList()
          .slice(0, 4);

        queryClient.invalidateQueries({
          queryKey: assetTypeKey,
        });

        queryClient.invalidateQueries({
          queryKey: typeDimension,
        });

        queryClient.invalidateQueries({
          queryKey: assetClassKey,
        });
      },
      onError: (error: Error) => {
        toast({
          title: `Failed to create asset type: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

export const useUpdateAssetType = () => {
  return useSmartMutation<any, Error, AssetTypePayload>(
    (payload: AssetTypePayload) => updateAssetType(payload),
    {
      onSuccess: () => {
        toast({
          title: "Asset type updated successfully!",
          variant: "default",
        });

        const assetTypeKey = QueryKeyFactory.module.ax.settings
          .assetTypeList()
          .slice(0, 3);
        const typeDimension = QueryKeyFactory.module.ax.settings
          .assetTypeDimensionList()
          .slice(0, 4);

        queryClient.invalidateQueries({
          queryKey: assetTypeKey,
        });

        queryClient.invalidateQueries({
          queryKey: typeDimension,
        });
      },
      onError: (error: Error) => {
        toast({
          title: `Failed to update asset type: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

// Asset Dimension Hooks
export const useTypeDimensionList = (queryParams?: Record<string, any>) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.settings.assetTypeDimensionList(queryParams),
    () => getTypeDimensionList(queryParams)
  );
};

export const useCreateAssetDimension = () => {
  return useSmartMutation((payload: any) => createAssetDimension(payload), {
    onSuccess: () => {
      toast({
        title: "Asset dimension created successfully!",
        variant: "default",
      });

      const key = QueryKeyFactory.module.ax.settings
        .assetDimensionList()
        .slice(0, 4);
      const typeListKey = QueryKeyFactory.module.ax.settings
        .assetTypeDimensionList()
        .slice(0, 4);
      queryClient.invalidateQueries({
        queryKey: key,
      });
      queryClient.invalidateQueries({
        queryKey: typeListKey,
      });
    },
    onError: (error: Error) => {
      toast({
        title: `Failed to create asset dimension: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};

export const useUpdateAssetDimension = () => {
  return useSmartMutation<any, Error, AssetDimensionPayload>(
    (payload: AssetDimensionPayload) => updateAssetDimension(payload),
    {
      onSuccess: () => {
        toast({
          title: "Asset dimension updated successfully!",
          variant: "default",
        });

        const key = QueryKeyFactory.module.ax.settings
          .assetDimensionList()
          .slice(0, 4);
        const typeListKey = QueryKeyFactory.module.ax.settings
          .assetTypeDimensionList()
          .slice(0, 4);
        queryClient.invalidateQueries({
          queryKey: key,
        });
        queryClient.invalidateQueries({
          queryKey: typeListKey,
        });
      },
      onError: (error: Error) => {
        toast({
          title: `Failed to update asset dimension: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

// Asset Failure Mode Hooks
export const useAssetFailureList = (code: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.settings.assetFailureList(code),
    () => getAssetFailureList(code),
    {
      enabled: Boolean(code),
    }
  );
};

export const useCreateAssetFailure = () => {
  return useSmartMutation<any, Error, AssetFailurePayload>(
    (payload: AssetFailurePayload) => createAssetFailure(payload),
    {
      onSuccess: () => {
        toast({
          title: "Asset failure mode created successfully!",
          variant: "default",
        });

        const key = QueryKeyFactory.module.ax.settings
          .assetFailureList()
          .slice(0, 4);
        const typeListKey = QueryKeyFactory.module.ax.settings
          .assetTypeList()
          .slice(0, 4);

        queryClient.invalidateQueries({
          queryKey: key,
        });

        queryClient.invalidateQueries({
          queryKey: typeListKey,
        });
      },
      onError: (error: Error) => {
        toast({
          title: `Failed to create asset failure mode: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

export const useUpdateAssetFailure = () => {
  return useSmartMutation<any, Error, AssetFailurePayload>(
    (payload: AssetFailurePayload) => updateAssetFailure(payload),
    {
      onSuccess: () => {
        toast({
          title: "Asset failure mode updated successfully!",
          variant: "default",
        });

        const key = QueryKeyFactory.module.ax.settings
          .assetFailureList()
          .slice(0, 4);
        queryClient.invalidateQueries({
          queryKey: key,
        });

        queryClient.refetchQueries({
          queryKey: key,
        });
      },
      onError: (error: Error) => {
        toast({
          title: `Failed to create asset failure mode: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

export const useToggleAssetClass = () => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartMutation(
    ({ code, isActive }: { code: string; isActive: boolean }) =>
      updateAssetClass({
        remote_utility_id: remoteUtilityId,
        code: code,
        is_active: isActive,
      }),
    {
      onSuccess: () => {
        toast({
          title: "Asset class updated successfully!",
          variant: "default",
        });

        const settingKey = QueryKeyFactory.module.ax.settings
          .assetClassSettingsList()
          .slice(0, 4);
        const key = QueryKeyFactory.module.ax.settings
          .assetClassList()
          .slice(0, 4);
        queryClient.refetchQueries({ queryKey: key });
        queryClient.refetchQueries({ queryKey: settingKey });
      },

      onError: (error: Error) => {
        toast({
          title: `Failed to update asset class: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

export const useToggleAssetType = () => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartMutation(
    ({ code, isActive }: { code: string; isActive: boolean }) =>
      updateAssetClass({
        remote_utility_id: remoteUtilityId,
        code: code,
        is_active: isActive,
      }),
    {
      onSuccess: () => {
        toast({
          title: "Asset type Updated successfully!",
          variant: "default",
        });

        const assetTypeKey = QueryKeyFactory.module.ax.settings
          .assetTypeList()
          .slice(0, 4);
        queryClient.invalidateQueries({
          queryKey: assetTypeKey,
        });

        queryClient.refetchQueries({
          queryKey: assetTypeKey,
        });
      },
      onError: (error: Error) => {
        toast({
          title: `Failed to update asset class: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

export const useDeleteFailureMode = () => {
  return useSmartMutation((code: string) => deleteAssetClass(code), {
    onSuccess: () => {
      toast({
        title: "Failure mode deleted successfully!",
        variant: "default",
      });

      const key = QueryKeyFactory.module.ax.settings
        .assetFailureList()
        .slice(0, 4);
      const typeListKey = QueryKeyFactory.module.ax.settings
        .assetTypeList()
        .slice(0, 4);

      queryClient.invalidateQueries({
        queryKey: key,
      });

      queryClient.invalidateQueries({
        queryKey: typeListKey,
      });
    },
    onError: (error: Error) => {
      toast({
        title: `Failed to update asset class: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};

export const useDimensionList = (code: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.settings.assetDimensionList(code),
    () => getDimensionList(code),
    {
      enabled: Boolean(code),
    }
  );
};

export const useSearchAssetClassList = (searchData: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.settings.assetSearchClassList(searchData),
    () => searchAssetClassList(searchData)
  );
};

export const useDeleteDimension = () => {
  return useSmartMutation((code: string) => deleteDimension(code), {
    onSuccess: () => {
      toast({
        title: "Dimension deleted successfully!",
        variant: "default",
      });

      const key = QueryKeyFactory.module.ax.settings
        .assetDimensionList()
        .slice(0, 4);
      const typeListKey = QueryKeyFactory.module.ax.settings
        .assetTypeDimensionList()
        .slice(0, 4);
      queryClient.invalidateQueries({
        queryKey: key,
      });
      queryClient.invalidateQueries({
        queryKey: typeListKey,
      });
    },
    onError: (error: Error) => {
      toast({
        title: `Failed to delete dimension: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};

export const useUpdateCondition = () => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartMutation((payload: any) => updateAssetClass(payload), {
    onSuccess: () => {
      toast({
        title: "Asset class updated successfully!",
        variant: "default",
      });

      const key = QueryKeyFactory.module.ax.settings.assetClassList();

      queryClient.invalidateQueries({ queryKey: key });
    },

    onError: (error: Error) => {
      toast({
        title: `Failed to update asset class: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};

export const useToggleConditionParameter = () => {
  return useSmartMutation((payload: any) => toggleConditionParameter(payload), {
    onSuccess: () => {
      toast({
        title: "Condition parameter updated successfully!",
        variant: "default",
      });

      const key = QueryKeyFactory.module.ax.shared.conditionList().slice(0, 4);
      queryClient.refetchQueries({ queryKey: key });
    },

    onError: (error: Error) => {
      toast({
        title: `Failed to update condition parameter: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};
